import {
  Component,
  Input,
  OnInit,
  ViewChild,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { GruposService } from '../../../../services/grupo/grupos.service';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { getPaginatorIntl } from '../../../../shared/mat-paginator-intl';
import { SelecionarEmpresasGrupoComponent } from '../../dialogs/selecionar-empresas-grupo/selecionar-empresas-grupo.component';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { ModalService } from '../../../../services/modal.service';
import { AuthService } from '../../../../services/auth/auth.service';

interface EmpresaGrupo {
  id: number;
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  estado: string;
  cidade: string;
}

@Component({
  selector: 'app-cadastrar-grupo-empresas',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: getPaginatorIntl,
    },
  ],
  templateUrl: './cadastrar-grupo-empresas.html',
  styleUrl: './cadastrar-grupo-empresas.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CadastrarGrupoEmpresas implements OnInit, AfterViewInit {
  @Input() idGrupo: number | null = null;
  @Input() readOnly: boolean = false;

  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<EmpresaGrupo>([]);

  // Configurações de paginação
  pageSize = 10;
  pageSizeOptions = [5, 10, 25];
  total = 0;
  inicio = 1;
  fim = 10;
  textoContagem = 'Nenhuma empresa vinculada';

  filtroNome = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private dialog: MatDialog,
    private gruposService: GruposService,
    private snackbarService: SnackbarService,
    private modalService: ModalService,
    private authService: AuthService
  ) { }

  async ngOnInit(): Promise<void> {
    // Define as colunas baseado na permissão do usuário e modo readOnly
    this.displayedColumns = (this.isMasterUser() && !this.readOnly)
      ? ['nomeFantasia', 'cnpj', 'estado', 'cidade', 'acoes']
      : ['nomeFantasia', 'cnpj', 'estado', 'cidade'];

    if (this.idGrupo) {
      await this.carregarEmpresas();
    } else {
      this.atualizarContagem(0, this.pageSize);
    }

    this.dataSource.filterPredicate = (data: EmpresaGrupo, filter: string) => {
      const filtros = JSON.parse(filter);

      const nomeOuCnpjMatch =
        data.nomeFantasia
          .toLowerCase()
          .includes(filtros.nome.toLowerCase()) ||
        data.cnpj.toLowerCase().includes(filtros.nome.toLowerCase());

      return nomeOuCnpjMatch;
    };
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  async carregarEmpresas(): Promise<void> {
    try {
      const empresas = await this.gruposService.buscarEmpresasDoGrupo(this.idGrupo!);
      this.dataSource.data = empresas;
      this.total = this.dataSource.data.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (err) {
      console.error('Erro ao carregar empresas do grupo:', err);
      this.total = 0;
      this.atualizarContagem(0, this.pageSize);
    }
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(
      event.pageIndex,
      event.pageSize,
      this.dataSource.filteredData.length
    );
  }

  atualizarContagem(pageIndex: number, pageSize: number, totalRows?: number) {
    const totalExibicao = totalRows ?? this.dataSource.data.length;
    const maxPageIndex = Math.max(0, Math.ceil(totalExibicao / pageSize) - 1);
    pageIndex = Math.min(pageIndex, maxPageIndex);

    const startItem = pageIndex * pageSize + 1;
    const endItem = Math.min((pageIndex + 1) * pageSize, totalExibicao);

    this.inicio = totalExibicao > 0 ? startItem : 0;
    this.fim = endItem;

    if (totalExibicao === 0) {
      this.textoContagem = this.idGrupo
        ? 'Nenhuma empresa vinculada ao grupo'
        : 'Salve os dados gerais para vincular empresas';
    } else if (totalExibicao === 1) {
      this.textoContagem = 'Mostrando 1 empresa';
    } else {
      this.textoContagem = `Mostrando ${this.inicio} a ${this.fim} de ${totalExibicao} empresas`;
    }
  }

  aplicarFiltro() {
    const filtro = {
      nome: this.filtroNome.trim(),
    };
    this.dataSource.filter = JSON.stringify(filtro);

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.atualizarContagem(
      0,
      this.pageSize,
      this.dataSource.filteredData.length
    );
  }

  adicionarEmpresas() {
    if (!this.idGrupo || this.readOnly) {
      // Se não tem grupo salvo ainda ou está em modo somente leitura, não pode adicionar empresas
      return;
    }

    const dialogRef = this.dialog.open(SelecionarEmpresasGrupoComponent, {
      width: '80vw',
      maxWidth: '1200px',
      height: 'auto',
      maxHeight: '90vh',
      data: {
        idGrupo: this.idGrupo,
      },
    });

    dialogRef.afterClosed().subscribe((empresasAdicionadas) => {
      if (empresasAdicionadas && empresasAdicionadas.length > 0) {
        // Recarrega a lista de empresas vinculadas
        this.carregarEmpresas();
      }
    });
  }

  formatarCnpj(cnpj: string): string {
    if (!cnpj) return '';
    
    // Remove tudo que não é dígito
    const apenasNumeros = cnpj.replace(/\D/g, '');
    
    // Aplica a máscara XX.XXX.XXX/XXXX-XX
    if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return cnpj; // Retorna o original se não tiver 14 dígitos
  }

  async removerEmpresaDoGrupo(empresaId: number, nomeEmpresa: string) {
    if (!this.idGrupo || this.readOnly) {
      return;
    }

    // Verifica se usuário tem permissão
    if (!this.authService.isMasterUser()) {
      this.snackbarService.error('Apenas usuários MASTER podem remover empresas do grupo');
      return;
    }

    this.modalService.open({
      title: 'Remover empresa do grupo',
      message: 'Ao clicar em remover, a empresa selecionada será <strong>removida desse grupo.</strong>',
      icon: 'warning',
      buttons: [
        { text: 'Remover', action: 'remove', color: 'warn', style: 'filled' }
      ]
    }).subscribe(async (result) => {
      if (result === 'remove') {
        try {
          await this.gruposService.removerEmpresaDoGrupo(this.idGrupo!, empresaId);
          this.snackbarService.success('Empresa removida do grupo com sucesso!');
          await this.carregarEmpresas(); // Recarrega a lista
        } catch (error) {
          console.error('Erro ao remover empresa do grupo:', error);
          this.snackbarService.error('Erro ao remover empresa do grupo');
        }
      }
    });
  }

  isMasterUser(): boolean {
    return this.authService.isMasterUser();
  }
}
